<template>
  <div class="pulley-viz-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>详见下方原理说明</div>
    </div>

    <div class="main-layout vertical">
      <!-- 渲染区 -->
      <div class="canvas-mixed interactive" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag">
        <svg width="100%" height="100%" viewBox="0 0 400 350">
           <!-- 天花板 -->
           <rect x="50" y="20" width="300" height="10" fill="#333" />
           <line x1="200" y1="30" x2="200" y2="60" stroke="#888" stroke-width="2" v-if="pulleyType !== 'movable'" />

           <!-- 滑轮渲染逻辑 -->
           <!-- 1. 定滑轮 -->
           <g v-if="pulleyType === 'fixed'">
              <circle cx="200" cy="80" r="20" fill="none" stroke="#4da1ff" stroke-width="4" />
              <line :x1="180" :y1="80 + pullOffset" x2="180" :y2="280 + pullOffset" stroke="#ff4757" stroke-width="2" />
              <line :x1="220" :y1="80" x2="220" :y2="200 - pullOffset" stroke="#00d2ff" stroke-width="2" />
              <rect :x="170" :y="280 + pullOffset" width="20" height="30" fill="#ff4757" rx="4" />
              <text :x="180" :y="300 + pullOffset" text-anchor="middle" fill="#fff" font-size="10">G</text>
              <circle :cx="220" :cy="200 - pullOffset" r="6" fill="#00d2ff" style="cursor: ns-resize" />
           </g>

           <!-- 2. 动滑轮 -->
           <g v-if="pulleyType === 'movable'">
              <line x1="160" y1="30" x2="160" :y2="180 + pullOffset/2" stroke="#888" stroke-width="2" />
              <circle cx="180" :cy="180 + pullOffset/2" r="20" fill="none" stroke="#4da1ff" stroke-width="4" />
              <line x1="200" :y1="180 + pullOffset/2" x2="200" :y2="80 - pullOffset" stroke="#00d2ff" stroke-width="2" />
              <rect x="170" :y="200 + pullOffset/2" width="20" height="30" fill="#ff4757" rx="4" />
              <circle cx="200" :cy="80 - pullOffset" r="6" fill="#00d2ff" style="cursor: ns-resize" />
           </g>

           <!-- 3. 滑轮组 (n=2) -->
           <g v-if="pulleyType === 'system'">
              <!-- 定滑轮 -->
              <circle cx="200" cy="60" r="15" fill="none" stroke="#4da1ff" stroke-width="3" />
              <!-- 动滑轮 -->
              <circle cx="200" :cy="180 + pullOffset/2.5" r="15" fill="none" stroke="#4da1ff" stroke-width="3" />
              <!-- 绳子 -->
              <path :d="`M 185 180 L 185 60 A 15 15 0 0 1 215 60 L 215 180 A 15 15 0 0 0 185 180`" fill="none" stroke="#888" stroke-width="1.5" :transform="`translate(0, ${pullOffset/2.5})`" />
              <line x1="215" :y1="180 + pullOffset/2.5" x2="240" :y2="80 - pullOffset" stroke="#00d2ff" stroke-width="2" />
              <rect x="190" :y="200 + pullOffset/2.5" width="20" height="30" fill="#ff4757" rx="4" />
              <circle cx="240" :cy="80 - pullOffset" r="6" fill="#00d2ff" style="cursor: ns-resize" />
           </g>
        </svg>

        <div class="hud-stats">
           <div class="row"><span>拉力 F:</span> <span class="val">{{ force.toFixed(1) }} N</span></div>
           <div class="row"><span>机械省力:</span> <span class="val">{{ advantage }} 倍</span></div>
        </div>
      </div>

      <!-- 参数控制区 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>物体重量 G: {{ weight }} N</label>
                  <el-slider v-model="weight" :min="10" :max="100" size="mini"></el-slider>
               </div>
               <div class="col center">
                  <el-button type="primary" size="mini" @mousedown.native="startAutoPull" @mouseup.native="stopAutoPull">点击持续拉动</el-button>
               </div>
            </div>
         </div>

         <div class="theory-note">
            <i class="el-icon-info"></i>
            <p v-if="pulleyType === 'fixed'">定滑轮：不省力，但可以改变力的方向。F = G</p>
            <p v-else-if="pulleyType === 'movable'">动滑轮：省一半力，但多费一倍距离。F = G/2</p>
            <p v-else>滑轮组：省力倍数取决于承担重物的绳子段数 n。F = G/n</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PulleySystemViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      pulleyType: 'fixed',
      weight: 40,
      pullOffset: 0,
      isDragging: false,
      autoPull: null
    }
  },
  computed: {
    advantage() {
      if (this.pulleyType === 'fixed') return 1;
      if (this.pulleyType === 'movable') return 2;
      return 2.5; // Simplified for the system demo
    },
    force() {
      return this.weight / this.advantage;
    }
  },
  methods: {
    startDrag() { this.isDragging = true; },
    stopDrag() { this.isDragging = false; },
    onDrag(e) {
      if (!this.isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const width = rect.width || 1;
      const height = rect.height || 1;
      const y = e.clientY - rect.top;
      this.pullOffset = Math.max(-50, Math.min(100, 150 - y * (350 / height)));
    },
    startAutoPull() {
      if (this.autoPull) clearInterval(this.autoPull);
      this.autoPull = setInterval(() => {
        this.pullOffset = (this.pullOffset + 1) > 100 ? -50 : (this.pullOffset + 1);
      }, 30);
    },
    stopAutoPull() {
      if (this.autoPull) clearInterval(this.autoPull);
    }
  },
  beforeDestroy() {
    if (this.autoPull) clearInterval(this.autoPull);
  }
}
</script>

<style scoped lang="scss">
.pulley-viz-pro {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}

.viz-header {
  display: flex; justify-content: space-between; align-items: center;
  .title { font-size: 1.1rem; color: #fff; font-weight: 300; }
}

.canvas-mixed {
  flex: 1.5; background: rgba(0,0,0,0.3); border-radius: 12px; border: 1px solid rgba(255,255,255,0.05); position: relative;
  .hud-stats {
    position: absolute; bottom: 15px; right: 15px; background: rgba(0,0,0,0.6); padding: 10px 15px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1);
    .row { font-size: 0.75rem; color: #888; display: flex; justify-content: space-between; gap: 15px; margin-bottom: 3px; .val { color: #00d2ff; font-family: monospace; } }
  }
}

.bottom-controls {
  flex: 0.5; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; padding-bottom: 5px;
}

.control-card {
  background: rgba(255, 255, 255, 0.03); border-radius: 12px; padding: 12px 18px;
  .row { display: grid; grid-template-columns: 1fr 120px; gap: 20px; align-items: center; }
  .col { label { display: block; font-size: 0.7rem; color: #aaa; margin-bottom: 5px; } &.center { display: flex; justify-content: center; } }
}

.theory-note {
  background: rgba(0, 210, 255, 0.05); border-radius: 12px; padding: 12px 15px; display: flex; gap: 10px; align-items: center;
  i { color: #00d2ff; }
  p { margin: 0; font-size: 0.75rem; color: #888; }
}
</style>
